import React, { useEffect, useRef, useState } from "react";
import {
  Row,
  Col,
  Modal,
  Button,
  Form,
  Table,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ErrorMessage, Formik } from "formik";
import { jwtDecode } from "jwt-decode";
import * as Yup from "yup";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { FiMinusCircle } from "react-icons/fi";
import url from "../../../configs/config";
import Card from "../../../components/Card";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import TextInput from "../../../components/input/edit-input-field";
import InputField from "../../../components/input/input-field";
import FadeLoaderComponent from "../../../components/loader-component";
import SearchInput from "../../../components/search-input";
import PaginationComponent from "../../../components/pagination/pagination-component";
import { roleTypes } from "../../../data/roleType";

const colors = [
  {
    status: "active",
    color: "bg-primary",
  },
  {
    status: "in-active",
    color: "bg-warning",
  },
];

const PermissionList = () => {
  const alertRef = useRef(null);
  const alertRefEdit = useRef(null);
  const [permissions, setPermissions] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [blockIsSubmit, setBlockIsSubmit] = useState(true);
  const [loader, setLoader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedData, setSelectedData] = useState();
  const [decodedToken, setDecodedToken] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalEntries, setTotalEntries] = useState(0);
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const apiCallInProgress = useRef(false);
  const initialLoadComplete = useRef(false);
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });

  useEffect(() => {
    if (token && typeof token === "string") {
      try {
        const decoded = jwtDecode(token);
        setDecodedToken(decoded?.user);
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token]);

  const getPermissions = async (offset = 0, limit = 10) => {
    if (apiCallInProgress.current) return;
    apiCallInProgress.current = true;
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      setLoader(false);
      return;
    } else {
      if (
        decodedToken?.roles?.some(
          (role) => role.role_type === roleTypes.SUPER_ADMIN,
        )
      ) {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
          params: {
            offset,
            limit,
            search:
              searchQuery.trim().length >= 2
                ? searchQuery.trim()
                : searchQuery.trim().length < 1
                  ? null
                  : null,
          },
        };

        try {
          const response = await axios.get(
            `${url.backendURL}permission/`,
            config,
          );
          setPermissions(response.data.response.data);
          setTotalEntries(response.data.response.permissionsCount);
          setLoader(false);
        } catch (err) {
          if (alertRef?.current) {
            alertRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
          handleError(err, "permissions");

          setLoader(false);
        } finally {
          apiCallInProgress.current = false;
          initialLoadComplete.current = true;
        }
      } else {
        apiCallInProgress.current = false;
        setLoader(false);
      }
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        },
      );
    } else {
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  useEffect(() => {
    if (!decodedToken) return;
    if (!initialLoadComplete.current) {
      getPermissions((currentPage - 1) * entriesPerPage, entriesPerPage);
    } else if (!apiCallInProgress.current) {
      getPermissions((currentPage - 1) * entriesPerPage, entriesPerPage);
    }
  }, [currentPage, entriesPerPage, decodedToken]);

  useEffect(() => {
    if (!initialLoadComplete.current) return;
    const debounceTimer = setTimeout(async () => {
      if (searchQuery.trim().length >= 2 || searchQuery.trim().length < 1) {
        setLoading(true);
        setCurrentPage(1);
        await getPermissions(0, entriesPerPage);

        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleShowEdit = async (e, item) => {
    e.preventDefault();
    setSelectedData({
      label: item?.label,
      slug: item?.slug,
      description: item?.description,
      permissions: item?.permissions || [],
      id: item?.id,
    });
    setShowEdit(true);
  };

  const handleCloseEdit = async () => {
    setShowEdit(false);
    setErrors();
  };

  const handleEditPermission = async (values, { setSubmitting, resetForm }) => {
    if (
      decodedToken?.roles?.some(
        (role) => role.role_type === roleTypes.SUPER_ADMIN,
      )
    ) {
      setSubmitting(true);
      try {
        if (!token) {
          localStorage.removeItem("coyote-token");

          navigate(
            `/${
              decodedToken?.roles?.some(
                (role) => role.role_type === roleTypes.SUPER_ADMIN,
              )
                ? "admin"
                : "user"
            }/auth/sign-in`,
            {
              state: { status: "invalid-token" },
            },
          );
          return;
        }
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

        const updatedPermissions = values.permissions.map((perm) => ({
          label: perm.label,
          permission_slug: perm.permission_slug,
          display: perm.display,
        }));

        const data = {
          label: values.label,
          description: values.description,
          permissions: updatedPermissions,
        };

        const response = await axios.put(
          `${url.backendURL}permission/${selectedData.id}`,
          data,
          config,
        );

        await getPermissions(
          (currentPage - 1) * entriesPerPage,
          entriesPerPage,
        );

        if (alertRefEdit?.current) {
          alertRefEdit.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        setAlert({
          message: response && response.data.message,
          type: "success",
          show: true,
          alertFor: "edit",
        });
        setTimeout(() => {
          setShowEdit(false);
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 2000);
      } catch (err) {
        if (alertRefEdit?.current) {
          alertRefEdit.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "edit");
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handlePermissionStatus = async (id, status) => {
    if (!token) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN,
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "invalid-token" },
        },
      );
      return;
    }

    if (
      decodedToken?.roles?.some(
        (role) => role.role_type === roleTypes.SUPER_ADMIN,
      )
    ) {
      try {
        const config = {
          headers: {
            Authorization: `${token}`,
          },
        };

        const data = {
          status: status,
        };
        const response = await axios.put(
          `${url.backendURL}permission/${id}/status`,
          data,
          config,
        );

        await getPermissions(
          (currentPage - 1) * entriesPerPage,
          entriesPerPage,
        );

        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }

        setAlert({
          message: response && response.data.message,
          type: "success",
          show: true,
          alertFor: "permissions",
        });
        setBlockIsSubmit(true);

        setTimeout(() => {
          setAlert({ show: false, alertFor: "", message: "", type: "" });
        }, 5000);
      } catch (err) {
        if (alertRef?.current) {
          alertRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
        handleError(err, "permissions");

        setBlockIsSubmit(true);
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    if (totalEntries === 0) return;
    setCurrentPage(pageNumber);
  };

  const handleEntriesPerPageChange = (e) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(totalEntries / entriesPerPage);

  const validationSchema = Yup.object().shape({
    label: Yup.string()
      .trim()
      .required("Label is required")
      .matches(/^[a-zA-Z\s]*$/, "Label can only contain letters and spaces"),
    permissions: Yup.array()
      .of(
        Yup.object().shape({
          label: Yup.string().required("Permission label is required"),
          permission_slug: Yup.string().required("Permission slug is required"),
          display: Yup.boolean().required("Display is required"),
        }),
      )
      .min(1, "At least one permission is required")
      .required("Permissions are required"),
    description: Yup.string()
      .trim()
      .required("Description is required")
      .min(1, "Description must be at least 1 character long")
      .max(255, "Description must not exceed 255 characters"),
  });

  const getColorByStatus = (status) => {
    const colorObject = colors.find((color) => color.status === status);
    return colorObject ? colorObject.color : "";
  };

   useEffect(() => {
    function toggleStickyNames() {
      const isWideScreen = window.innerWidth > 650;

      const stickyHeadNames = document.querySelectorAll(".sticky-head-name");
      const stickyDataNames = document.querySelectorAll(".sticky-data-names");

      if (isWideScreen) {
        stickyHeadNames.forEach((head) => {
          head.classList.add("sticky-action-head-buttons");
          const parentRow = head.closest(".tr-user");
          if (parentRow) {
            head.style.height = `${parentRow.offsetHeight}px`;
          }
        });
        stickyDataNames.forEach((data) => {
          data.classList.add("sticky-action-buttons");
          const parentRow = data.closest(".tr-user");
          if (parentRow) {
            data.style.height = `${parentRow.offsetHeight}px`;
          }
        });
      } else {
        stickyHeadNames.forEach((head) => {
          head.classList.remove("sticky-action-head-buttons");
          head.style.height = "auto";
        });
        stickyDataNames.forEach((data) => {
          data.classList.remove("sticky-action-buttons");
          data.style.height = "auto";
        });
      }
    }
    toggleStickyNames();
    window.addEventListener("resize", toggleStickyNames);
    return () => window.removeEventListener("resize", toggleStickyNames);
  }, [loader, permissions]);

  return (
    <>
       <style>
        {`
          .table-user .sticky-action-buttons {
            background-color: #ffffff !important;
            z-index: 2;
          }
          .table-user .tr-user:hover .sticky-action-buttons {
            background-color: #f5f6fa !important;
          }
          .table-user .sticky-action-head-buttons {
            background: #e2e4e9 !important;
          }
          .table-user .sticky-action-head-buttons,
          .table-user .sticky-action-buttons {
            left: 0;
            z-index: 11;
            position: absolute;
            width: 260px;
            height: inherit;
            display: flex;
            align-items: center;
            padding-left: 15px;
            border-bottom: 1px solid #ebedef !important;
          }
          .table-user .sticky-action-buttons {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .table-user .th-user:nth-child(2),
          .table .td-user:nth-child(2) {
            padding-left: 275px !important;
          }
           @media (max-width: 650px) {
            .table-user .sticky-action-head-buttons,
            .table-user .sticky-action-buttons {
               position: static !important;
               width: auto !important;
               white-space: normal !important;
            }
            .table-user .th-user:nth-child(2),
            .table .td-user:nth-child(2) {
              padding-left: 0.75rem !important;
            }
          }
        `}
      </style>
      <div>
        <div className="bd-example">
          <div className="d-flex justify-content-between flex-wrap pl-0">
            <Modal
              show={showEdit}
              onHide={handleCloseEdit}
              backdrop="true"
              keyboard={true}
              size="xl"
            >
              <Modal.Header className="custom-modal-header">
                <Modal.Title>Edit Permission</Modal.Title>
                <button
                  type="button"
                  className="btn-close-svg"
                  onClick={handleCloseEdit}
                  aria-label="Close"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </Modal.Header>
              <Formik
                initialValues={{
                  label: selectedData?.label || "",
                  permissions: selectedData?.permissions || [],
                  description: selectedData?.description || "",
                }}
                validationSchema={validationSchema}
                onSubmit={handleEditPermission}
                enableReinitialize
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  handleSubmit,
                  isSubmitting,
                }) => (
                  <Form className="form-horizontal p-3" onSubmit={handleSubmit}>
                    <FocusError />

                    <Modal.Body ref={alertRefEdit}>
                      {alert.alertFor === "edit" && (
                        <AlertNotificationSimple
                          type={alert.type}
                          message={alert.message}
                          show={alert.show}
                          onClose={() => setAlert({ ...alert, show: false })}
                        />
                      )}
                      <Row>
                        <Col sm="6">
                          <InputField
                            label="Label"
                            name="label"
                            placeholder="Enter Label"
                            value={values.label}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={true}
                            asterick={true}
                            asRow={Col}
                            disabled={false}
                            errors={errors?.label}
                          />
                        </Col>

                        <Col sm="6">
                          <Form.Group className="form-group">
                            <Form.Label>
                              Permissions <span className="text-danger">*</span>
                            </Form.Label>
                            <div
                              style={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "1rem",
                              }}
                            >
                              {values.permissions?.map((permission, index) => (
                                <Form.Check
                                  key={index}
                                  type="checkbox"
                                  id={`permission-${index}`}
                                  label={permission.label}
                                  checked={permission.display}
                                  onChange={(e) => {
                                    const updatedPermissions = [
                                      ...values.permissions,
                                    ];
                                    updatedPermissions[index] = {
                                      ...updatedPermissions[index],
                                      display: e.target.checked,
                                    };
                                    setFieldValue(
                                      "permissions",
                                      updatedPermissions,
                                    );
                                  }}
                                  style={{ cursor: "pointer" }}
                                />
                              ))}
                            </div>
                            <ErrorMessage
                              name="permissions"
                              component="div"
                              className="text-danger"
                            />
                            {errors?.permissions?.map((error, index) => (
                              <span
                                key={index}
                                className="w-100 mt-1 small text-danger"
                              >
                                {error}
                                <br />
                              </span>
                            ))}
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm="12">
                          <InputField
                            label="Description"
                            name="description"
                            as="textarea"
                            value={values.description}
                            placeholder="Enter Description"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            required={true}
                            errors={errors?.description}
                            disabled={false}
                            asRow={Col}
                            asterick={true}
                          />
                        </Col>
                      </Row>
                    </Modal.Body>

                    <Modal.Footer>
                      <Button
                        type="submit"
                        variant="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        Submit
                      </Button>
                      <Button
                        type="button"
                        variant="btn btn-danger"
                        onClick={handleCloseEdit}
                      >
                        Cancel
                      </Button>
                    </Modal.Footer>
                  </Form>
                )}
              </Formik>
            </Modal>
          </div>
        </div>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header className="d-flex justify-content-between listing-card-header">
                <div className="header-title">
                  <h4 className="card-title">Permission List</h4>
                </div>
              </Card.Header>
              <div className="d-flex align-items-center justify-content-between">
                <Row className="align-items-center d-flex justify-content-between mt-2 w-100">
                  {/* Show entries section */}
                  <Col
                    sm="5"
                    md="4"
                    lg="4"
                    xs="12"
                    ref={alertRef}
                    className="order-1"
                  >
                    <div className="pt-4 d-flex align-items-center col-12 ms-4 listing-entries">
                      <label className="me-2">Show</label>
                      <select
                        className="form-select py-1 ps-2 pe-0 text-sm bg-white rounded-lg w-25"
                        value={entriesPerPage}
                        onChange={handleEntriesPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                      <span className="ms-2">entries</span>
                    </div>
                  </Col>

                  <Col sm="7" md="8" lg="8" xs="12" className="order-2">
                    <div className="pt-4 d-flex justify-content-start align-items-center">
                      <Row className="g-2 ms-4 ms-sm-0 align-items-end w-100 justify-content-end listing-controls-row">
                        {/* Search Input */}
                        <Col
                          xs="12"
                          sm="auto"
                          md="auto"
                          lg="auto"
                          className="d-flex ps-0 me-0 me-sm-0 flex-column flex-md-row align-items-start align-items-md-center listing-search-col"
                        >
                          <label className="me-2 mb-1 mb-md-0 text-nowrap">
                            Search:
                          </label>
                          <div
                            style={{ maxWidth: "180px", width: "100%" }}
                            className="listing-search-input-wrapper pe-sm-0 pe-3"
                          >
                            <SearchInput
                              searchQuery={searchQuery}
                              setSearchQuery={setSearchQuery}
                              loading={loading}
                              setLoading={setLoading}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Col>
                </Row>
              </div>
              <Card.Body className="px-0">
                {alert.alertFor === "permissions" && (
                  <Row>
                    <Col className="mt-2 mb-2 ms-4 me-4">
                      <AlertNotificationSimple
                        type={alert.type}
                        message={alert.message}
                        show={alert.show}
                        onClose={() => setAlert({ ...alert, show: false })}
                      />
                    </Col>{" "}
                  </Row>
                )}
              <div style={{ overflowX: "auto", width: "100%" }}>
                  <Table
                    id="permission-list-table "
                   className="table-user"
                    role="grid"
                    responsive
                    
                    data-toggle="data-table"
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#e2e4e9" }} className="tr-user">
                        <th style={{ width: "20%" }} className="sticky-head-name th-user">Label</th>
                        <th style={{ width: "40%" }} className="th-user">Description</th>
                        <th style={{ width: "20%" }} className="th-user">Status</th>
                        <th style={{ width: "20%" }} className="th-user">Action</th>
                      </tr>
                    </thead>
                    {!loader && permissions.length > 0 ? (
                      <tbody>
                        {permissions?.map((item, idx) => {
                          return (
                            <tr key={idx}  className=" tr-user">
                              <td className="sticky-data-names td-user">{item.label}</td>
                              <td
                                className="description-cell td-user"
                                style={{ textWrap: "auto" }}
                              >
                                {item.description &&
                                item.description.length > 100
                                  ? `${item.description.substring(0, 100)}...`
                                  : item.description}
                              </td>
                              <td className="td-user">
                                <span
                                  className={`badge ${getColorByStatus(
                                    item.status,
                                  )}`}
                                >
                                  {item.status}
                                </span>
                              </td>
                              <td style={{ textWrap: "unset" }} className="td-user">
                                <div className="flex align-items-center list-user-action">
                                  <Link
                                    className={`btn btn-sm btn-icon ${
                                      item?.status === "in-active"
                                        ? "btn-success"
                                        : "btn-danger"
                                    } `}
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (blockIsSubmit) {
                                        setBlockIsSubmit(false);
                                        handlePermissionStatus(
                                          item.id,
                                          item.status === "active"
                                            ? "in-active"
                                            : "active",
                                        );
                                      }
                                    }}
                                    title={
                                      item?.status === "in-active"
                                        ? "Active"
                                        : "In-active"
                                    }
                                    data-original-title={
                                      item?.status === "in-active"
                                        ? "Active"
                                        : "In-active"
                                    }
                                  >
                                    <span className="btn-inner">
                                      {item?.status === "in-active" ? (
                                        <CheckCircleOutlineIcon
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                          }}
                                        />
                                      ) : (
                                        <FiMinusCircle
                                          style={{
                                            width: "20px",
                                            height: "20px",
                                          }}
                                        />
                                      )}
                                    </span>
                                  </Link>{" "}
                                  <Link
                                    className={`btn btn-sm btn-icon btn-warning `}
                                    data-toggle="tooltip"
                                    data-placement="top"
                                    title="Edit"
                                    data-original-title="Edit"
                                    onClick={(e) => {
                                      handleShowEdit(e, item);
                                    }}
                                  >
                                    <span className="btn-inner">
                                      <svg
                                        width="20"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <path
                                          d="M11.4925 2.78906H7.75349C4.67849 2.78906 2.75049 4.96606 2.75049 8.04806V16.3621C2.75049 19.4441 4.66949 21.6211 7.75349 21.6211H16.5775C19.6625 21.6211 21.5815 19.4441 21.5815 16.3621V12.3341"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        ></path>
                                        <path
                                          fillRule="evenodd"
                                          clipRule="evenodd"
                                          d="M8.82812 10.921L16.3011 3.44799C17.2321 2.51799 18.7411 2.51799 19.6721 3.44799L20.8891 4.66499C21.8201 5.59599 21.8201 7.10599 20.8891 8.03599L13.3801 15.545C12.9731 15.952 12.4211 16.181 11.8451 16.181H8.09912L8.19312 12.401C8.20712 11.845 8.43412 11.315 8.82812 10.921Z"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        ></path>
                                        <path
                                          d="M15.1655 4.60254L19.7315 9.16854"
                                          stroke="currentColor"
                                          strokeWidth="1.5"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        ></path>
                                      </svg>
                                    </span>
                                  </Link>{" "}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    ) : loader ? (
                      <tbody>
                        <tr>
                          <td colSpan={4}>
                            <FadeLoaderComponent />
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody>
                        <tr>
                          <td colSpan={4}>
                            <div className="d-flex w-100 justify-content-center">
                              <p>No permission found</p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    )}
                  </Table>
                </div>
                <Row className="align-items-center mt-2">
                  <Col sm="6">
                    <div className="d-flex flex-wrap items-center ms-4">
                      <div className="">
                        Showing{" "}
                        {Math.min(
                          (currentPage - 1) * entriesPerPage + 1,
                          totalEntries,
                        )}{" "}
                        to{" "}
                        {Math.min(currentPage * entriesPerPage, totalEntries)}{" "}
                        of {totalEntries} entries
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="bd-example d-flex justify-content-center justify-content-md-end me-4">
                      <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default PermissionList;
